import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowLeft,
	ArrowRight,
	Bot,
	Building2,
	ChartNoAxesCombined,
	ClipboardList,
	Leaf,
	Store,
	Tractor,
	Wallet,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { CowIcon } from "@/components/icons/CowIcon";

export const Route = createFileRoute("/_auth/app/_app/help/user-guide/")({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation();

	const guideItems = [
		{
			key: "establishments",
			title: t("help.guideEstablishmentsTitle"),
			description: t("help.guideEstablishmentsDescription"),
			icon: Building2,
			to: "/app/help/user-guide/establishments",
			color: "text-blue-600",
			bgColor: "bg-blue-50",
		},
		{
			key: "livestock",
			title: t("help.guideLivestockTitle"),
			description: t("help.guideLivestockDescription"),
			icon: CowIcon,
			to: "/app/help/user-guide/livestock",
			color: "text-amber-600",
			bgColor: "bg-amber-50",
		},
		{
			key: "grassProduction",
			title: t("help.guideGrassProductionTitle"),
			description: t("help.guideGrassProductionDescription"),
			icon: Leaf,
			to: "/app/help/user-guide/grass-production",
			color: "text-green-600",
			bgColor: "bg-green-50",
		},
		{
			key: "equipment",
			title: t("help.guideEquipmentTitle"),
			description: t("help.guideEquipmentDescription"),
			icon: Tractor,
			to: "/app/help/user-guide/equipment",
			color: "text-orange-600",
			bgColor: "bg-orange-50",
		},
		{
			key: "financial",
			title: t("help.guideFinancialTitle"),
			description: t("help.guideFinancialDescription"),
			icon: Wallet,
			to: "/app/help/user-guide/financial",
			color: "text-emerald-600",
			bgColor: "bg-emerald-50",
		},
		{
			key: "planning",
			title: t("help.guidePlanningTitle"),
			description: t("help.guidePlanningDescription"),
			icon: ClipboardList,
			to: "/app/help/user-guide/planning",
			color: "text-indigo-600",
			bgColor: "bg-indigo-50",
		},
		{
			key: "feasibility",
			title: t("help.guideFeasibilityTitle"),
			description: t("help.guideFeasibilityDescription"),
			icon: ChartNoAxesCombined,
			to: "/app/help/user-guide/feasibility",
			color: "text-purple-600",
			bgColor: "bg-purple-50",
		},
		{
			key: "marketplace",
			title: t("help.guideMarketplaceTitle"),
			description: t("help.guideMarketplaceDescription"),
			icon: Store,
			to: "/app/help/user-guide/marketplace",
			color: "text-pink-600",
			bgColor: "bg-pink-50",
		},
		{
			key: "assistant",
			title: t("help.guideAssistantTitle"),
			description: t("help.guideAssistantDescription"),
			icon: Bot,
			to: "/app/help/user-guide/assistant",
			color: "text-cyan-600",
			bgColor: "bg-cyan-50",
		},
	];

	return (
		<div className="p-6 max-w-6xl mx-auto">
			{/* Mobile Back Button */}
			<Link
				to="/app/help"
				className="md:hidden flex items-center gap-2 text-primary mb-4"
			>
				<ArrowLeft className="w-4 h-4" />
				{t("common.back")}
			</Link>

			{/* Header */}
			<div className="mb-8">
				<h1 className="text-3xl font-bold text-base-content mb-2">
					{t("help.userGuideOverview")}
				</h1>
				<p className="text-base sm:text-lg text-base-content/70">
					{t("help.userGuideOverviewDescription")}
				</p>
			</div>

			{/* Guide Sections Grid */}
			<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
				{guideItems.map((item) => {
					const Icon = item.icon;
					return (
						<Link
							key={item.key}
							to={item.to}
							className="group bg-base-100 p-6 rounded-lg border border-base-300 hover:shadow-lg hover:border-primary/20 transition-all duration-200"
						>
							<div
								className={`inline-flex p-3 rounded-lg ${item.bgColor} mb-4 group-hover:scale-110 transition-transform duration-200`}
							>
								<Icon className={`h-6 w-6 ${item.color}`} />
							</div>
							<h3 className="text-lg font-semibold text-base-content mb-2 group-hover:text-primary transition-colors">
								{item.title}
							</h3>
							<p className="text-base-content/70 text-sm mb-3">
								{item.description}
							</p>
							<div className="flex items-center text-primary text-sm font-medium group-hover:translate-x-1 transition-transform duration-200">
								{t("common.learnMore")}
								<ArrowRight className="h-4 w-4 ml-1" />
							</div>
						</Link>
					);
				})}
			</div>
		</div>
	);
}
